import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private termsAgreedSubject = new BehaviorSubject<boolean>(false);
  termsAgreed$: Observable<boolean> = this.termsAgreedSubject.asObservable();

  private currentStepSubject = new BehaviorSubject<number>(1);
  currentStep$: Observable<number> = this.currentStepSubject.asObservable();

  private customers: any[] = [];

  private businessLicenseFilesSubject = new BehaviorSubject<any[]>([]);
  private agreedBySubject = new BehaviorSubject<string>('');

  constructor() {
    // Load saved customers from localStorage on service initialization
    const savedCustomers = localStorage.getItem('customers');
    if (savedCustomers) {
      this.customers = JSON.parse(savedCustomers);
    }
  }

  setTermsAgreed(value: boolean): void {
    this.termsAgreedSubject.next(value);
  }

  getTermsAgreed(): Observable<boolean> {
    return this.termsAgreed$;
  }

  setCurrentStep(step: number): void {
    this.currentStepSubject.next(step);
  }

  getCurrentStep(): Observable<number> {
    return this.currentStep$;
  }

  getCustomerByEmail(email: string): any {
    return this.customers.find((customer) => customer.billingEmail === email);
  }

  saveCustomer(customerData: any): void {
    const existingIndex = this.customers.findIndex(
      (customer) => customer.billingEmail === customerData.billingEmail
    );

    if (existingIndex !== -1) {
      this.customers[existingIndex] = customerData;
    } else {
      this.customers.push(customerData);
    }

    localStorage.setItem('customers', JSON.stringify(this.customers));
  }

  // Save the form data to session storage
  saveFormData(formData: any): void {
    try {
      // Don't save if the formData is empty or undefined
      if (!formData || Object.keys(formData).length === 0) {
        console.warn('Attempted to save empty form data');
        return;
      }

      // console.log('Saving form data:', formData);

      // Create a deep clone of the form data
      let dataToSave = JSON.parse(JSON.stringify(formData));

      // Handle users array - always use the latest data only
      if (dataToSave.users && Array.isArray(dataToSave.users)) {
        // Only save complete user data
        dataToSave.users = dataToSave.users.filter((user: any) => {
          return (
            user &&
            user.username &&
            user.firstName &&
            user.lastName &&
            user.emailId
          );
        });

        // console.log('Storing only current users:', dataToSave.users.length);
      }

      // Save to session storage - always overwrite with latest data
      // console.log('Form data saved successfully', dataToSave);

      // Update terms agreed state if relevant data is present
      // Use setTimeout to break the potential circular update cycle
      if (dataToSave.agreed !== undefined) {
        setTimeout(() => {
          this.setTermsAgreed(!!dataToSave.agreed);
        }, 0);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }

  // Retrieve only the core form data from session storage
  getFormData(): any | null {
    try {
      const savedData = sessionStorage.getItem('customerFormData');
      return savedData ? JSON.parse(savedData) : null;
    } catch (error) {
      console.error('Error retrieving form data:', error);
      return null;
    }
  }

  clearFormData(): void {
    // console.log('Clearing all form data from storage');
    sessionStorage.removeItem('customerFormData');
    localStorage.removeItem('formState');
    this.setTermsAgreed(false);
    this.setCurrentStep(1);

    // Also clear any other related data that might have been stored
    sessionStorage.removeItem('lastFormStep');
    sessionStorage.removeItem('uploadedFiles');
  }

  // Save the navigation state to track internal vs. external navigation
  saveFormNavigationState(isInternalNavigation: boolean): void {
    try {
      // Record the navigation state
      localStorage.setItem(
        'formNavigationState',
        JSON.stringify({
          isInternalNavigation,
          lastActive: new Date().toISOString(),
          lastStep: this.currentStepSubject.value,
        })
      );
    } catch (error) {
      console.error('Error saving form navigation state:', error);
    }
  }

  // Check if the user is returning from internal navigation
  isReturningFromInternalNavigation(): boolean {
    try {
      const stateJson = localStorage.getItem('formNavigationState');
      if (!stateJson) return false;

      const state = JSON.parse(stateJson);
      return state.isInternalNavigation === true;
    } catch {
      return false;
    }
  }

  getBusinessLicenseFiles(): Observable<any[]> {
    return this.businessLicenseFilesSubject.asObservable();
  }

  setBusinessLicenseFiles(files: any[]): void {
    this.businessLicenseFilesSubject.next(files);
  }

  getAgreedBy(): Observable<string> {
    return this.agreedBySubject.asObservable();
  }

  setAgreedBy(name: string): void {
    this.agreedBySubject.next(name);
  }
}
